import {
  SheetHeader,
  SheetTitle,
  usePreventSheetClose,
} from "@/components/ui/sheet";
import { format } from "date-fns";
import { ReportExpense, useUpdateExpenseMutation } from "@common/utils/graphql";
import { useForm } from "react-hook-form";
import { SheetBackButton } from "@common/components/SheetActions/SheetBackButton";
import { addTimezoneOffsetToUTCZeroTimeIfNeeded } from "@common/components/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { hasDirtyFields, receiptInputData } from "@/utils/utils";
import { useUpdateExpenseOutOfPolicyState } from "@common/state/outOfPolicyState";
import { ExpenseFormValues, expenseFormSchemaObject } from "../forms/Forms";
import { ReimbursementDetailsForm } from "./ReimbursementDetailsForm";
import { useCustomFieldFormConfig } from "@common/components/CustomFields";
import { z } from "zod";
import { sanatizeCustomFieldEntries } from "@common/components/CustomFields";
import { mergeExpenseFormValues } from "@common/utils";
import { useRef } from "react";

interface Props {
  onBack?: () => void;
  expense: ReportExpense;
}

export const EditReimbursementSheetContent = ({ onBack, expense }: Props) => {
  const { customFieldSchema, customFieldDefaultValues } =
    useCustomFieldFormConfig(expense?.customFieldEntries);
  const didPrefillWithOcr = useRef(false);
  const schema = z.object({
    ...expenseFormSchemaObject,
    ...customFieldSchema,
  });
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      amount: expense.amount,
      attendeeEmails: expense.attendeeEmails,
      date: addTimezoneOffsetToUTCZeroTimeIfNeeded(expense.transactionDate),
      expenseCategoryGLId: expense.expenseCategoryGLId
        ? String(expense.expenseCategoryGLId)
        : undefined,
      id: expense.id,
      merchant: expense.merchantName,
      notes: expense.notes ? String(expense.notes) : undefined,
      receipt: expense.receiptFileUrl,
      reportId: String(expense.reportId),
      ...customFieldDefaultValues,
    },
  });

  const [updateExpense, { loading }] = useUpdateExpenseMutation();
  const updateExpenseOutOfPolicyState = useUpdateExpenseOutOfPolicyState();
  usePreventSheetClose(() => hasDirtyFields(form) || didPrefillWithOcr.current);

  const handlePrefillForm = (formValues: Partial<ExpenseFormValues>) => {
    didPrefillWithOcr.current = true;
    const prevValues = form.getValues();
    form.reset(mergeExpenseFormValues(prevValues, formValues));
  };

  const submitChanges = async () => {
    const output = await form.trigger(undefined, { shouldFocus: true });
    const values = form.getValues();

    if (!output) {
      return;
    }

    // eslint-disable-next-line prefer-const
    let [error, receiptFileData] = await receiptInputData(values.receipt);
    if (error) return;

    const isReceiptRemoved = !!expense?.receiptFileUrl && !values.receipt;
    if (isReceiptRemoved) {
      receiptFileData = null;
    }
    await updateExpense({
      variables: {
        expenseId: values.id!,
        merchantName: values.merchant,
        expenseCategoryGLId: values.expenseCategoryGLId
          ? Number(values.expenseCategoryGLId)
          : undefined,
        amount: Number(values.amount),
        transactionDate: format(values.date, "yyyy-MM-dd"),
        notes: values.notes,
        receiptFileData,
        reportId: Number(values.reportId),
        attendeeEmails: values.attendeeEmails,
        customFieldEntries: sanatizeCustomFieldEntries(
          values.customFieldEntries,
        ),
      },
    });
    updateExpenseOutOfPolicyState(expense.id);
    if (onBack) {
      onBack();
    }
  };

  return (
    <>
      <SheetBackButton onClick={onBack!} />
      <SheetHeader>
        <SheetTitle>Edit Reimbursement</SheetTitle>
      </SheetHeader>
      <div className="flex flex-1 flex-col px-sheet-gap pb-sheet-gap">
        <ReimbursementDetailsForm
          form={form}
          onSubmit={submitChanges}
          submitText={loading ? "Saving Changes..." : "Save Changes"}
          loading={loading}
          onParseReceipt={handlePrefillForm}
        />
      </div>
    </>
  );
};
